import 'whatwg-fetch';
import 'url-search-params-polyfill';
import React from 'react';
import ReactDOM from 'react-dom';
import { setOptions, overrideComponent } from '@sheerid/jslib';
import './index.css';
import App from './App';
import { StepSuccessComponent } from './StepSuccessComponent';
import { StepDocUploadComponent } from './StepDocUploadComponent';
import { StepPendingComponent } from './StepPendingComponent';
import { StepStudentPersonalInfoComponent } from './StepStudentPersonalInfoComponent';

setOptions({
    restApi: {
        serviceUrl: 'https://services.sheerid.com/',
    },
    logLevel: 'error',
    messagesWithLocale: {
        'en-US': {
            "verifyMyMilitaryStatus": 'SUBMIT FOR REVIEW',
            "branchOfServicePlaceholder": "Branch of Service",
            "collegeName": "University / College name",
            "companyName": "Vineyard Vines",
            "dateTime": {
                "january": "January",
                "february": "February",
                "march": "March",
                "april": "April",
                "may": "May",
                "june": "June",
                "july": "July",
                "august": "August",
                "september": "September",
                "october": "October",
                "november": "November",
                "december": "December",
                "month": "Month",
                "day": "Day",
                "year": "Year"
            },
            "firstName": " ",
            "lastName": " ",
            "emailAddress": " ",
            "verificationPurposesOnly": " ",
            "emailExplanation": " ",
            "branchOfService": " ",
            "error": "Error",
            "howDoesReviewWork": "How does review work?",
            "howDoesVerifyingWork": "How does verifying work?",
            "loading": "Loading...",
            "militaryStatus": "Military status",
            "noOptions": "No options",
            "personalInformation": "Personal Information",
            "personalOrSchoolIsFine": "Personal or school email is fine",
            "postalCode": " ",
            "poweredBy": 'Verification services powered by SheerID.',
            "requiredFields": "All fields are required",
            "school": "School",
            "schoolName": "School Name",
            "sheerIdFaqs": "SheerID FAQs",
            "studentInfoShared": "Information entered into this web form will be used for verification purposes. The information will be shared with Vineyard Vines.",
            "universityName": "University name",
            "verifyAndContinue": "Verify and continue",
            "errorId": {
                "internalServerError": "Internal server error",
                "noRemainingRewardCodes": "Sorry, this offer is closed and we can't verify you at this time. Please try again later.",
                "noProgram": "No program found",
                "noVerification": "No verification found",
                "invalidProgram": "Invalid program",
                "invalidStep": "Invalid step",
                "invalidMilitaryStatus": "Invalid military status, expected one of [ACTIVE_DUTY, VETERAN, RESERVIST, MILITARY_RETIREE]",
                "invalidFirstResponderStatus": "Invalid first responder status, expected one of [POLICE, EMT, FIREFIGHTER]",
                "invalidOrganization": "You must select an organization from the list",
                "invalidFirstName": "Invalid first name",
                "invalidLastName": "Invalid last name",
                "invalidBirthDate": "Invalid birth date",
                "invalidEmail": "Invalid email",
                "invalidPostalCode": "Invalid postal code",
                "underagePerson": "Underage",
                "outsideAgePerson": "Age outside allowed range",
                "futureBirthDate": "Future birthdate",
                "unsupportedDocMimeType": "One or more files is an unsupported format",
                "invalidFileSizeEmpty": "One or more files is empty",
                "invalidFileSizeMax": "One or more files is too large (max: 10MB)",
                "invalidNumberOfFiles": "Maximum number of files exceeded (max: 5)",
                "invalidDocUploadToken": "Provided document upload token is not valid",
                "verificationLimitExceeded": "Verification limit exceeded",
                "docReviewLimitExceeded": "Document review limit exceeded",
                "maxMetadataValuesExceeded": "Maximum number of metadata values exceeded",
                "maxMetadataLengthExceeded": "Maximum length of metadata value exceeded",
                "unknownError": "Unknown Error",
                "invalidRequest": "Invalid request",
                "invalidApiToken": "Provided API token was invalid or empty",
                "simulatedError": "This is a simulated error",
                "notFound": "Not found"
            },
            "step": {
                "personalInfo": {
                    "subtitle": "To confirm your eligibility, complete the form below.",
                    "title": " ",
                    "reqField": "All fields are required.",
                    "howDoesVerifyingWorkDetails": "To be eligible for this offer, you must currently be enrolled in a United States-based, degree-granting, Title IV-accreditted school of higher education.<br/><br/>{companyName} uses SheerID, a trusted partner, to verify that you are a current student. SheerID only collects information that assists in verifying your eligibility. Additional information or documentation may be requested.",
                    "countries": {
                        "US": "United States"
                    },
                    "locales": {
                        "en-US": "English"
                    }
                },
                "docUpload": {
                    "acceptedTypes": "File must be .jpg .png .pdf .gif or .bmp",
                    "title": "Upload Documentation",
                    "ACTIVE_DUTY": "Active duty",
                    "VETERAN": "Veteran",
                    "RESERVIST": "Reservist",
                    "MILITARY_RETIREE": "Military Retiree",
                    "MILITARY_FAMILY": "Military Family",
                    "subtitle": "To complete verification, please submit any document that clearly shows your name and your specific military affiliation.",
                    "footer": "Please omit, cover up or black out any sensitive information such as Social Security Number, Military ID#, etc. The information we DO need to be visible: first name, last name, valid dates and/or status, for example: \"retired\" or \"active duty\".",
                    "includeInUpload1": "Your full name",
                    "includeInUpload2": "Your branch of service",
                    "includeInUpload3": "Your affiliation",
                    "redirect1": "If your name, branch of service, or affiliation",
                    "redirect2": "are not correct, please click here to enter your information again.",
                    "requirementsUpload": "Requirements for successful document review:",
                    "submitButtonLabel": "SUBMIT FOR REVIEW",
                    "noFileText": "No file selected",
                    "fileInstructions": "Choose File",
                    "howDoesReviewWorkDetails": "Document review is handled by SheerID, a trusted partner who specializes in verifying students.<br/><br/>Documents are used only for verifying your student status is current and will be permanently deleted after review is finalized. They will not be shared with {companyName}.",
                    "uploadInstructions": "Important - Your document must display the following:",
                    "acceptableFormats": ".bmp, .gif, .jpg, .png, or .pdf.",
                    "acceptableFormatsTitle": "Acceptable formats are: ",
                    "acceptableExamples": "Any document that shows you have met the qualifications of military service and were honorably discharged.",
                    "estimatedWaitTime": "a few minutes",
                    "rejectionReasons": {
                        "DOCUMENT_UNREADABLE": "The document you submitted was unreadable due to poor image quality. Please make sure you have a clearly legible image that's bright and crisp, and try submitting again.",
                        "DOCUMENT_PASSWORD_PROTECTED": "We are unable to accept password protected documents.",
                        "DOCUMENT_LIKELY_FRAUD": "The document you submitted is insufficient for establishing eligibility for this offer.",
                        "DOCUMENT_UNSUPPORTED": "The document you submitted is not on the list of accepted document types.",
                        "DOCUMENT_HANDWRITTEN": "We cannot accept handwritten documents.",
                        "DOCUMENT_WORD_DOC": "We cannot accept text copied and pasted from official documents. Please submit a screenshot or image of the official document.",
                        "MISSING_INFORMATION": "The document you submitted does not provide all of the information necessary to determine eligibility for this offer.",
                        "MISSING_INFORMATION_PERSON_NAME": "The document you submitted is missing a first and/or last name.",
                        "MISSING_INFORMATION_ORGANIZATION_NAME": "The document you submitted does not include an organization name that confirms your connection to that organization.",
                        "DOCUMENT_EXPIRED": "The document you submitted has expired.",
                        "DOCUMENT_OUTDATED": "The document you submitted is missing an issue date or was issued too far in the past.",
                        "DOCUMENT_OUTDATED_LAST_3_MONTHS": "The document you submitted is missing an issue date or was issued more than three months ago.",
                        "DOCUMENT_OUTDATED_LAST_6_MONTHS": "The document you submitted is missing an issue date or was issued more than six months ago.",
                        "DOCUMENT_OUTDATED_LAST_12_MONTHS": "The document you submitted is missing an issue date or was issued more than twelve months ago.",
                        "MISMATCH_PERSON_NAME": "The information on the document you submitted does not match the first and/or last name submitted on the verification form.",
                        "MISMATCH_ORGANIZATION": "The organization name on the document(s) you submitted does not match the organization you listed on the verification form.",
                        "MISMATCH_STATUS": "The status on the document you submitted does not match the status you listed on your verification form.",
                        "INELIGIBLE_ORGANIZATION": "The organization on the document you submitted is not eligible for this offer.",
                        "MISSING_INFORMATION_UNIVERSITY_ENROLLMENT": "The document you submitted does not prove enrollment in the current term.",
                        "MISMATCH_SCHOOL_NAME": "The school name on the document you submitted does not match the school name listed on the verification form.",
                        "INELIGIBLE_ORGANIZATION_UNIVERSITY_TITLE_IV": "The school listed on the document you submitted is not eligible for this offer because it is not a Title IV degree-granting college or university in the United States.",
                        "INELIGIBLE_ORGANIZATION_US_UNIVERSITY": "The school listed in the document you submitted is not eligible for this offer because it is not in the United States.",
                        "INELIGIBLE_PERSON_HIGH_SCHOOL_STUDENT": "High School Students are not eligible for this offer.",
                        "MISSING_INFORMATION_AFFILIATION_US_ARMED_FORCES": "The document you submitted is does not show affiliation to an eligible branch of the United States Armed Forces.",
                        "MISSING_INFORMATION_AFFILIATION_US_UNIFORMED_SERVICES": "The document you submitted does not show affiliation to an eligible branch of the United States Uniformed Services.",
                        "MISMATCH_COMPANY_NAME": "The company name on the document you submitted does not match the company name you listed on the verification form.",
                        "PAYSTUB_OUTDATED_LAST_30_DAYS": "Please upload a paystub that was issued within the last 30 days.",
                        "PAYSTUB_OUTDATED_LAST_60_DAYS": "Please upload a paystub that was issued within the last 60 days.",
                        "MISSING_INFORMATION_BIRTHDATE": "The document you submitted is missing a birthdate, or your age is ineligible for this offer.",
                        "MISMATCH_BIRTHDATE": "The birthdate on the document you submitted does not match the birthdate you listed on the verification form.",
                        "OTHER_CONTACT_US": "We are having problems verifying your eligibility for the offer. Please contact SheerID customer service for help: customerservice@sheerid.com",
                        "PERSON_FIELD_MISMATCH": "A field in the document provided did not match with what was provided on the verification form."
                    }
                },
                "pending": {
                    "subtitle": "Your document has uploaded successfully. The review process has now started. You will be contacted via email in less than 20 minutes with details regarding your verification request.",
                    "title": "Upload success",
                    "subtitle2": "NOTE: If you do not receive an email within the time shown above, you may want to check your junk/spam folder for the message.",
                },
                "success": {
                    "title": "20% off Military offer",
                    "subtitle": "You've been verified. Below is your single-use coupon code that is ready for you to use right now. Your single-use coupon code can be used during checkout on Vineyard Vines website. The coupon code cannot be combined with any other offer or promotion.",
                    "redirectUrl": "<a href='https://www.vineyardvines.com'>www.vineyardvines.com</a>",
                    "redirectButtonText": "Continue",
                    "redirectText1": "Please return to ",
                    "redirectText2": " to continue shopping.",
                    "emailNotification": "To make sure you have every opportunity to use your discount, we’re also emailing you a copy of your code and a link to the Vineyard Vines website so that you can shop now, or shop later. Please check for an email from us (verify@sheerid.com) for all the details."
                }
            }
        }
    }
});

// @ts-ignore
overrideComponent('StepStudentPersonalInfoComponent', StepStudentPersonalInfoComponent);
overrideComponent('StepSuccessComponent', StepSuccessComponent);
overrideComponent('StepDocUploadComponent', StepDocUploadComponent);
overrideComponent('StepPendingComponent', StepPendingComponent);

ReactDOM.render(<App />, document.getElementById('root'));
